import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cotizacionesApi } from '@/services/cotizacionesApi';
import { formatCurrency, formatDate } from '@/utils/formatters';
import { TIPO_OPERACION_LABELS, ESTADO_COTIZACION_LABELS, ESTADO_COLORS } from '@/utils/constants';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import type { EstadoCotizacion } from '@/types/cotizacion';
import { generateCotizacionPdf } from '@/utils/pdfGenerator';

const ESTADOS: EstadoCotizacion[] = ['BORRADOR', 'ENVIADA', 'ACEPTADA', 'RECHAZADA'];

export function Historial() {
  const [page, setPage] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState<number | null>(null);
  const queryClient = useQueryClient();

  const handleGeneratePdf = async (id: number) => {
    setGeneratingPdf(id);
    try {
      const cot = await cotizacionesApi.findById(id);
      await generateCotizacionPdf(cot);
    } catch (err) {
      console.error('Error generando PDF:', err);
      toast.error('Error al generar el PDF');
    } finally {
      setGeneratingPdf(null);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['cotizaciones', page],
    queryFn: () => cotizacionesApi.findAll(page, 10),
  });

  const estadoMutation = useMutation({
    mutationFn: ({ id, estado }: { id: number; estado: EstadoCotizacion }) =>
      cotizacionesApi.updateEstado(id, estado),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cotizaciones'] });
      toast.success('Estado actualizado');
    },
    onError: () => toast.error('Error al actualizar estado'),
  });

  return (
    <div className="max-w-6xl mx-auto">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Historial de Cotizaciones</h2>

      {/* Desktop: table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">N° Cotización</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cliente</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Estado</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-500">Cargando...</td>
              </tr>
            ) : data?.content.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-500">No hay cotizaciones</td>
              </tr>
            ) : (
              data?.content.map((cot) => (
                <tr key={cot.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{cot.id}</td>
                  <td className="px-4 py-3 text-sm font-mono text-blue-700">{cot.numeroCotizacion}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(cot.fechaCreacion)}</td>
                  <td className="px-4 py-3 text-sm">{cot.cliente.nombre}</td>
                  <td className="px-4 py-3 text-sm">{TIPO_OPERACION_LABELS[cot.tipoOperacion]}</td>
                  <td className="px-4 py-3 text-sm font-medium">{formatCurrency(cot.totalFinal)}</td>
                  <td className="px-4 py-3">
                    <select
                      value={cot.estadoCotizacion}
                      onChange={(e) => estadoMutation.mutate({ id: cot.id, estado: e.target.value as EstadoCotizacion })}
                      className={`px-2 py-0.5 rounded-full text-xs font-medium border-none outline-none cursor-pointer ${ESTADO_COLORS[cot.estadoCotizacion]}`}
                    >
                      {ESTADOS.map((est) => (
                        <option key={est} value={est}>{ESTADO_COTIZACION_LABELS[est]}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleGeneratePdf(cot.id)}
                      disabled={generatingPdf === cot.id}
                      className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
                      title="Descargar PDF"
                    >
                      {generatingPdf === cot.id
                        ? <span className="text-xs">...</span>
                        : <FileText size={16} />
                      }
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile: cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : data?.content.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No hay cotizaciones</p>
        ) : (
          data?.content.map((cot) => (
            <div key={cot.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">#{cot.id}</span>
                  <span className="text-xs font-mono text-blue-700">{cot.numeroCotizacion}</span>
                </div>
                <select
                  value={cot.estadoCotizacion}
                  onChange={(e) => estadoMutation.mutate({ id: cot.id, estado: e.target.value as EstadoCotizacion })}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium border-none outline-none cursor-pointer ${ESTADO_COLORS[cot.estadoCotizacion]}`}
                >
                  {ESTADOS.map((est) => (
                    <option key={est} value={est}>{ESTADO_COTIZACION_LABELS[est]}</option>
                  ))}
                </select>
              </div>
              <p className="text-sm font-medium text-gray-900">{cot.cliente.nombre}</p>
              <div className="flex items-center justify-between mt-2 text-sm text-gray-500">
                <span>{formatDate(cot.fechaCreacion)}</span>
                <span>{TIPO_OPERACION_LABELS[cot.tipoOperacion]}</span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-sm font-bold text-gray-900">{formatCurrency(cot.totalFinal)}</span>
                <button
                  onClick={() => handleGeneratePdf(cot.id)}
                  disabled={generatingPdf === cot.id}
                  className="text-blue-600 hover:text-blue-800 disabled:opacity-40"
                  title="Descargar PDF"
                >
                  {generatingPdf === cot.id
                    ? <span className="text-xs">...</span>
                    : <FileText size={16} />
                  }
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between px-1 sm:px-4 py-3 mt-3 sm:mt-0 sm:border-t border-gray-200">
          <span className="text-sm text-gray-500">
            Pág. {data.number + 1} de {data.totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={data.number === 0}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={data.number >= data.totalPages - 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 hover:bg-gray-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
